import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { logout } from '../services/authService';
import { IDLE_ACTIVITY_KEY, readIdleActivity, writeIdleActivity, clearIdleActivity } from '../utils/sessionActivity';

// Idle auto sign-out (founder request). Stratia holds student PII and is often
// used on shared/school computers, so a walk-away must not leave an open
// session. A 3-hour idle window (long enough not to interrupt a paused essay
// session) with a 2-minute "still there?" warning before the sign-out, which
// then runs the same COMPLETE logout as the nav button (ends the session +
// lands on `/`). Activity is shared across tabs via a localStorage timestamp,
// so working in one tab keeps every tab alive, and the clock survives reloads
// and sleep (checked on mount + on tab focus).
export const IDLE_TIMEOUT_MS = 3 * 60 * 60 * 1000;   // 3 hours
export const WARN_BEFORE_MS = 2 * 60 * 1000;          // warn 2 min before
const CHECK_INTERVAL_MS = 20 * 1000;                  // poll cadence
const WRITE_THROTTLE_MS = 15 * 1000;                  // throttle activity writes
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'pointermove', 'click'];

export default function SessionTimeout({
    timeoutMs = IDLE_TIMEOUT_MS,
    warnMs = WARN_BEFORE_MS,
    checkMs = CHECK_INTERVAL_MS,
}) {
    const { currentUser } = useAuth();
    const enabled = !!currentUser;
    const [warning, setWarning] = useState(false);
    const lastWriteRef = useRef(0);
    const loggingOutRef = useRef(false);

    const doLogout = useCallback(() => {
        if (loggingOutRef.current) return;
        loggingOutRef.current = true;
        clearIdleActivity();
        logout(); // complete sign-out + redirect to landing
    }, []);

    const staySignedIn = useCallback(() => {
        lastWriteRef.current = Date.now();
        writeIdleActivity(lastWriteRef.current);
        setWarning(false);
    }, []);

    useEffect(() => {
        if (!enabled) return undefined;

        // Seed on (re)entry; a fresh sign-in starts the clock now.
        if (readIdleActivity() == null) writeIdleActivity(Date.now());
        lastWriteRef.current = Date.now();

        // Any real interaction bumps the shared clock (throttled) and clears a
        // showing warning. Same-value setState bails out — no re-render churn.
        const bump = () => {
            const t = Date.now();
            if (t - lastWriteRef.current >= WRITE_THROTTLE_MS) {
                lastWriteRef.current = t;
                writeIdleActivity(t);
            }
            setWarning((w) => (w ? false : w));
        };

        const check = () => {
            const last = readIdleActivity();
            if (last == null) { writeIdleActivity(Date.now()); return; }
            const idle = Date.now() - last;
            if (idle >= timeoutMs) doLogout();
            else if (idle >= timeoutMs - warnMs) setWarning(true);
            else setWarning((w) => (w ? false : w));
        };

        ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, bump, { passive: true }));
        const interval = setInterval(check, checkMs);
        const onVisible = () => { if (document.visibilityState === 'visible') check(); };
        document.addEventListener('visibilitychange', onVisible);
        // Activity in another tab writes the key → re-check here (keeps all
        // tabs alive together and dismisses a stale warning).
        const onStorage = (ev) => { if (ev.key === IDLE_ACTIVITY_KEY) check(); };
        window.addEventListener('storage', onStorage);

        check(); // immediate: catches a reload/wake after a long idle

        return () => {
            ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, bump));
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('storage', onStorage);
        };
    }, [enabled, timeoutMs, warnMs, checkMs, doLogout]);

    if (!enabled || !warning) return null;

    const mins = Math.max(1, Math.round(warnMs / 60000));
    return (
        <div
            className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            role="alertdialog" aria-modal="true" aria-labelledby="idle-title" data-testid="idle-warning"
        >
            <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="px-6 pt-6">
                    <div className="flex items-start gap-4">
                        <div className="p-3 rounded-full bg-amber-100">
                            <ClockIcon className="h-6 w-6 text-amber-600" />
                        </div>
                        <div className="flex-1 pt-1">
                            <h3 id="idle-title" className="text-lg font-semibold text-gray-900">Are you still there?</h3>
                            <p className="mt-2 text-sm text-gray-600">
                                You&rsquo;ve been inactive for a while. For your security we&rsquo;ll sign you out in
                                about {mins} minute{mins === 1 ? '' : 's'}. Choose &ldquo;Stay signed in&rdquo; to keep working.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="flex gap-3 px-6 py-4 mt-4 bg-gray-50 border-t border-gray-100">
                    <button
                        type="button" onClick={doLogout}
                        className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Sign out now
                    </button>
                    <button
                        type="button" onClick={staySignedIn}
                        className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg bg-[#1A4D2E] hover:bg-[#163F26] transition-colors"
                    >
                        Stay signed in
                    </button>
                </div>
            </div>
        </div>
    );
}
